/**
 * A room's gaming-day start, stored as minutes after local midnight (0–1439) and edited as a clock
 * time (ADR 0012 Decision 9). A number box reading 360 asks an administrator to do arithmetic about
 * their own room's opening hours; a time input reading 06:00 does not.
 *
 * <p>
 * <b>The server is the control</b> — <code>RoomGamingDay.Validate</code> refuses anything outside the
 * day with <code>invalid_gaming_day_start</code>. What this file decides is only how the value is
 * shown and read back, and that a cleared box is never mistaken for midnight.
 * </p>
 */

const pad = (n: number) => String(n).padStart(2, '0');

/** Minutes after midnight as the HH:mm a time input shows. */
export const clockOf = (minutes: number): string => `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;

/**
 * An HH:mm back into minutes — or <b>null</b> for anything that is not a time within one day. A time
 * input reports <code>''</code> when cleared, and reading that as 0 would save "midnight" for a room
 * whose administrator had only cleared the box on the way to typing something else. Null is what the
 * dialog refuses to save.
 */
export const minutesOf = (clock: string): number | null => {
  const match = /^(\d{2}):(\d{2})$/.exec(clock);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours < 24 && minutes < 60 ? hours * 60 + minutes : null;
};

/**
 * What the setting does, in the room's terms. At midnight the calendar is followed and there is
 * nothing more to say; past it, the sentence names the start and the case it exists for. The
 * example is fixed at 02:00 on a Saturday because that is the case — a Friday session running into
 * the small hours — and it is only true when the start is later than 02:00.
 */
export const gamingDayNote = (minutes: number): string => {
  if (minutes === 0) return 'The gaming day follows the calendar: it starts at midnight.';
  const start = clockOf(minutes);
  return minutes > 120
    ? `The gaming day starts at ${start}, so activity at 02:00 on a Saturday counts as Friday's.`
    : `The gaming day starts at ${start}, so activity before ${start} counts as the previous day's.`;
};
